
import { useState } from 'react';

interface Job {
  id: number;
  facilityType: string;
  facilityName: string;
  location: string;
  address: string;
  access: string;
  hourlyWage: string;
  workingHours: string;
  holidays: string;
  tags: string[];
  benefits: string[];
  requirements: string[];
  jobDescription: string;
  applicationMethod: string;
  image: string;
}

interface JobEditFormProps {
  job: Job;
  onClose: () => void;
  onUpdate: (updatedJob: Job) => void;
}

export default function JobEditForm({ job, onClose, onUpdate }: JobEditFormProps) {
  const [formData, setFormData] = useState({
    facilityName: job.facilityName,
    facilityType: job.facilityType,
    location: job.location,
    address: job.address,
    access: job.access,
    hourlyWage: job.hourlyWage,
    workingHours: job.workingHours,
    holidays: job.holidays,
    tags: job.tags.join(', '),
    benefits: job.benefits.join('\n'),
    requirements: job.requirements.join('\n'),
    jobDescription: job.jobDescription,
    applicationMethod: job.applicationMethod,
    image: job.image
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (name === 'image') {
      setImageError(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const updatedJob: Job = {
      ...job,
      facilityName: formData.facilityName,
      facilityType: formData.facilityType,
      location: formData.location,
      address: formData.address,
      access: formData.access,
      hourlyWage: formData.hourlyWage,
      workingHours: formData.workingHours,
      holidays: formData.holidays,
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      benefits: formData.benefits.split('\n').map(b => b.trim()).filter(b => b),
      requirements: formData.requirements.split('\n').map(r => r.trim()).filter(r => r),
      jobDescription: formData.jobDescription,
      applicationMethod: formData.applicationMethod,
      image: formData.image
    };
    
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      onUpdate(updatedJob);
    }, 2000);
  };

  const handlePublish = async () => {
    setIsSaving(true);
    
    try {
      const updatedJob: Job = {
        ...job,
        facilityName: formData.facilityName,
        facilityType: formData.facilityType,
        location: formData.location,
        address: formData.address,
        access: formData.access,
        hourlyWage: formData.hourlyWage,
        workingHours: formData.workingHours,
        holidays: formData.holidays,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        benefits: formData.benefits.split('\n').map(b => b.trim()).filter(b => b),
        requirements: formData.requirements.split('\n').map(r => r.trim()).filter(r => r),
        jobDescription: formData.jobDescription,
        applicationMethod: formData.applicationMethod,
        image: formData.image
      };

      await onUpdate(updatedJob);
    } catch (error) {
      console.error('公開に失敗しました:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">求人情報を編集</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {showSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg mb-6">
              <div className="flex items-center">
                <div className="w-5 h-5 flex items-center justify-center mr-3">
                  <i className="ri-check-line text-xl"></i>
                </div>
                <span className="font-medium">求人情報を更新しました</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                チラシ画像URL
              </label>
              <input
                type="text"
                name="image"
                value={formData.image}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                placeholder="https://trust-job.com/images/xxxxx.jpg"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                trust-job.com、imgBB、Imgur、その他の画像ホスティングサービスのURLに対応しています
              </p>
              {formData.image && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-2">プレビュー:</p>
                  <div className="w-full max-w-md h-96 bg-gray-100 rounded-lg overflow-hidden">
                    {!imageError ? (
                      <img 
                        src={formData.image}
                        alt="プレビュー"
                        className="w-full h-full object-cover object-top"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <div className="text-center">
                          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2">
                            <i className="ri-image-line text-4xl"></i>
                          </div>
                          <p className="text-sm">画像を読み込めません</p>
                          <p className="text-xs mt-1">URLを確認してください</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  施設名
                </label>
                <input
                  type="text"
                  name="facilityName"
                  value={formData.facilityName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  施設の種類
                </label>
                <select
                  name="facilityType"
                  value={formData.facilityType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base cursor-pointer"
                  required
                >
                  <option value="特養">特養</option>
                  <option value="住宅型">住宅型</option>
                  <option value="介護付き">介護付き</option>
                  <option value="デイサービス">デイサービス</option>
                  <option value="グループホーム">グループホーム</option>
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  勤務地（市区町村）
                </label>
                <textarea
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  時給
                </label>
                <textarea
                  name="hourlyWage"
                  value={formData.hourlyWage}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                住所
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                アクセス
              </label>
              <textarea
                name="access"
                value={formData.access}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                タグ（カンマ区切り）
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                placeholder="急募, 駅近, Wワーク可"
                required
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  勤務時間
                </label>
                <textarea
                  name="workingHours"
                  value={formData.workingHours}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  休日
                </label>
                <textarea
                  name="holidays"
                  value={formData.holidays}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                仕事内容
              </label>
              <textarea
                name="jobDescription"
                value={formData.jobDescription}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                待遇・福利厚生（1行に1つ）
              </label>
              <textarea
                name="benefits"
                value={formData.benefits}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                応募資格（1行に1つ）
              </label>
              <textarea
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                応募方法
              </label>
              <textarea
                name="applicationMethod"
                value={formData.applicationMethod}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                required
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                className="bg-teal-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                更新する
              </button>
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                キャンセル
              </button>
            </div>
          </form>

          {/* 公開ボタン */}
          <div className="fixed top-32 right-8 z-50">
            <button
              onClick={handlePublish}
              disabled={isSaving}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-medium shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSaving ? '保存中...' : '公開'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
